/**
 * Hierarchical Router with Slot-Based Composition
 * 
 * This router uses a tree-based approach where routes are composed hierarchically:
 * 1. All routes use prefix matching (match their path and all child paths)
 * 2. For a URL like /app/project/1/info, we find all matching routes:
 *    - /app/layout.js
 *    - /app/project/[projectId]/body.js
 *    - /app/project/[projectId]/info/content.js
 * 3. We build a parent-child tree structure
 * 4. We render from leaves to root:
 *    - Render info/content.js first
 *    - Pass that to project/body.js as the 'content' slot
 *    - Pass project/body.js output to app/layout.js as the 'body' slot
 * 5. The root output is rendered to the DOM
 * 
 * No special filename conventions needed - just hierarchical composition!
 */
import { render, html } from 'lit-html';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';

// Module-level state
let allRoutes = [];
let currentUrl = new URL(window.location.href);
let outlets = {};

/**
 * Registers the routes for the application.
 * This is called by the route discovery mechanism in `routes/index.js`.
 * @param {Array} routes - The array of processed route objects.
 */
export function registerRoutes(routes) {
  allRoutes = routes;
}

/**
 * Initializes the router with named outlets.
 * @param {Object} outletElements - Map of slot names to DOM elements (e.g., { body: element, head: element })
 */
export async function initRouter(outletElements = {}) {
  outlets = outletElements;

  window.addEventListener('popstate', () => {
    handleNavigation(new URL(window.location.href));
  });

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented) return;

    // Use composedPath to find anchors even inside Shadow DOM
    const path = e.composedPath();
    const anchor = path.find(el => el.tagName === 'A');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    const target = anchor.getAttribute('target');

    // Ignore external links, links with targets, and special protocols
    if (!href || target || href.startsWith('http') || href.startsWith('//')) {
      return;
    }

    // Allow links to opt out of SPA routing for full page navigation
    if (anchor.hasAttribute('data-native')) {
      return;
    }

    e.preventDefault();
    
    // Use SPA-friendly path resolution for relative paths
    const resolvedPath = resolveRelativePath(href);
    const newUrl = new URL(resolvedPath, window.location.origin);
    
    // Preserve existing history state (e.g. filters, selectedRoleId) so that
    // parent routes reading window.history.state are not reset on tab/link clicks.
    const currentState = history.state || {};
    history.pushState(currentState, '', newUrl);
    handleNavigation(newUrl);
  });

  await handleNavigation(currentUrl);
}

/**
 * The core navigation handler that orchestrates the rendering.
 * Uses hierarchical composition: renders from leaf to root, passing child content up.
 * @param {URL} url The URL to navigate to.
 */
async function handleNavigation(url) {
  currentUrl = url;
  // Use only pathname for route matching, ignoring query strings
  const path = url.pathname;

  const { matches, params } = findMatchingRoutes(path);

  if (matches.length === 0) {
    console.error('No routes matched the path:', path);
    render('Page not found', outlets.body || document.body);
    return;
  }

  // Sort matches from shallowest (root) to deepest (leaf) by depth
  matches.sort((a, b) => a.depth - b.depth);

  // Build the route tree: each route is a node that may have children
  // We'll render from leaf to root, composing children into parents
  const routeTree = buildRouteTree(matches, path);

  // Render from leaf to root, passing rendered children up the tree
  const { slots } = await renderRouteTree(routeTree, params);

  // Render each slot to its corresponding outlet
  for (const slotName of Object.keys(outlets)) {
    const outlet = outlets[slotName];
    if (!outlet) continue;
    
    const content = slots[slotName];
    
    const template = typeof content === 'string'
      ? html`${unsafeHTML(content)}`
      : content || html``; // Render empty if no content
    
    render(template, outlet);
  }
}

/**
 * Builds a tree structure from the matched routes
 * @param {Array} matches - Sorted array of matched routes (shallowest to deepest)
 * @param {string} path - The current URL path
 * @returns {Object} Root node of the route tree
 */
function buildRouteTree(matches, path) {
  // Create a tree where each node knows its children
  const nodes = matches.map(route => ({
    route,
    children: [],
    isLeaf: false
  }));

  // Link parent-child relationships
  for (let i = 0; i < nodes.length; i++) {
    const currentNode = nodes[i];
    const currentPath = currentNode.route.path;
    
    // Find the nearest ancestor routes (may not be immediate parent directory)
    // A route is a child of another if its path starts with the parent path
    // and is deeper (longer). We want all routes at the shallowest matching level.
    let parentDepth = -1;
    
    for (let j = i - 1; j >= 0; j--) {
      const potentialParent = nodes[j];
      const parentPath = potentialParent.route.path;
      
      // Check if current path is a descendant of this parent
      if (currentPath.startsWith(parentPath + '/') || (parentPath === '/' && currentPath.startsWith('/'))) {
        // If we haven't found a parent yet, or this parent is at the same depth as previous ones
        if (parentDepth === -1) {
          parentDepth = potentialParent.route.depth;
          potentialParent.children.push(currentNode);
        } else if (potentialParent.route.depth === parentDepth) {
          // Multiple routes at the same level (e.g., body.js and title.js)
          potentialParent.children.push(currentNode);
        } else if (potentialParent.route.depth > parentDepth) {
          // This is a closer ancestor, reset and use this level
          parentDepth = potentialParent.route.depth;
          potentialParent.children.push(currentNode);
        }
        // If parent is shallower than what we've found, skip it
      }
    }
  }

  // Mark leaf nodes (deepest matches in each branch)
  const deepestDepth = Math.max(...nodes.map(n => n.route.depth));
  nodes.forEach(node => {
    if (node.route.depth === deepestDepth || node.children.length === 0) {
      node.isLeaf = true;
    }
  });

  // If there are multiple roots (routes with no parents), create a virtual root
  const rootNodes = nodes.filter((node, index) => {
    // A node is a root if no other node has it as a child
    return !nodes.some(n => n.children.includes(node));
  });

  if (rootNodes.length === 1) {
    return rootNodes[0];
  } else {
    // Create a virtual root that contains all actual roots as children
    return {
      route: { path: '', slot: '__virtual_root__', module: { default: () => null } },
      children: rootNodes,
      isLeaf: false
    };
  }
}

/**
 * Recursively renders the route tree from leaves to root, maintaining a
 * persistent slot bag so every ancestor can access all descendant slots.
 * @param {Object} node - Current node to render
 * @param {Object} params - URL parameters
 * @returns {Promise<{rendered: any, slots: Record<string, any>}>
 * } Rendered content and the accumulated slots
 */
async function renderRouteTree(node, params) {
  const slots = {};
  const renderCache = new Map();

  const traverse = (currentNode) => {
    // Skip the virtual root (it has no component to render)
    if (currentNode.route.slot === '__virtual_root__') {
      return Promise.all(currentNode.children.map(child => traverse(child))).then(() => null);
    }

    // Deduplicate: return existing promise if we've already started rendering this exact route
    const routeKey = `${currentNode.route.path}:${currentNode.route.slot}`;
    if (renderCache.has(routeKey)) {
      return renderCache.get(routeKey);
    }

    const promise = (async () => {
      await Promise.all(currentNode.children.map(child => traverse(child)));

      const component = currentNode.route.module.default;
      if (typeof component !== 'function') {
        console.warn(`Module for slot "${currentNode.route.slot}" at path "${currentNode.route.path}" does not have a default export function.`);
        return null;
      }

      try {
        const renderedComponent = await component({ params, slots: { ...slots } });

        if (typeof renderedComponent !== 'string' && renderedComponent?._$litType$ === undefined && renderedComponent !== null && renderedComponent !== undefined) {
          console.warn(`Route for slot "${currentNode.route.slot}" returned non-string/non-template value.`);
        }

        if (currentNode.route.slot && renderedComponent !== null && renderedComponent !== undefined) {
          // Parent routes replace child slot values after composing them
          slots[currentNode.route.slot] = renderedComponent;
        }
        
        return renderedComponent;
      } catch (error) {
        console.error(`Error rendering route at "${currentNode.route.path}":`, error);
        return null;
      }
    })();

    renderCache.set(routeKey, promise);
    return promise;
  };

  const rootRendered = await traverse(node);
  return { rendered: rootRendered, slots };
}

/**
 * Re-renders the current route with updated store state
 * Used by the store subscription to trigger reactive updates
 */
export function rerenderCurrentRoute() {
  handleNavigation(currentUrl);
}

/**
 * Finds all route modules that match the given path and extracts URL parameters.
 * @param {string} path The URL pathname.
 * @returns {{matches: Array, params: Object}}
 */
function findMatchingRoutes(path) {
  const matches = [];
  let params = {};

  for (const route of allRoutes) {
    const match = route.regex.exec(path);
    if (match) {
      matches.push(route);

      // Extract params from the URL
      if (route.params.length > 0) {
        const routeParams = {};
        route.params.forEach((paramName, index) => {
          routeParams[paramName] = match[index + 1];
        });
        Object.assign(params, routeParams);
      }
    }
  }
  return { matches, params };
}


// --- Public API ---

/**
 * Resolves a path relative to the current location.
 * Follows standard browser URL semantics with a special case for "./" prefix.
 * Query strings and hashes are preserved in the returned URL.
 * 
 * Examples from /app/projects/1/info:
 * - "casting/new" → /app/projects/1/casting/new (replaces last segment - sibling)
 * - "./casting/new" → /app/projects/1/info/casting/new (appends - child route)
 * - "../casting/new" → /app/projects/casting/new (up one level)
 * - "/app/other" → /app/other (absolute)
 * - "/app/other?foo=bar" → /app/other?foo=bar (query string preserved)
 * 
 * @param {string} href The path to resolve
 * @returns {string} The resolved absolute path with query string and hash preserved
 */
function resolveRelativePath(href) {
  // Extract query string and hash to preserve them
  const queryIndex = href.indexOf('?');
  const hashIndex = href.indexOf('#');
  let cleanHref = href;
  let queryAndHash = '';
  
  if (queryIndex !== -1) {
    queryAndHash = href.slice(queryIndex);
    cleanHref = href.slice(0, queryIndex);
  } else if (hashIndex !== -1) {
    queryAndHash = href.slice(hashIndex);
    cleanHref = href.slice(0, hashIndex);
  }
  
  // If it's an absolute path, use it as-is
  if (cleanHref.startsWith('/')) {
    return cleanHref + queryAndHash;
  }

  // Special case: "./" prefix means append to current path (child route)
  if (cleanHref.startsWith('./')) {
    const currentPath = window.location.pathname;
    const base = currentPath.endsWith('/') ? currentPath.slice(0, -1) : currentPath;
    const remaining = cleanHref.slice(2); // Remove "./"
    return base + '/' + remaining + queryAndHash;
  }
  
  // For all other relative paths, use standard browser URL resolution
  // Add trailing slash to treat current path as a directory, not a file
  // This ensures ".." goes up one level correctly (e.g., from /talent/schedule to /talent/)
  const currentPath = window.location.pathname;
  const base = currentPath.endsWith('/') ? window.location.href : window.location.href + '/';
  const resolved = new URL(cleanHref, base);
  return resolved.pathname + queryAndHash;
}

/**
 * Programmatically navigates to a new URL.
 * @param {string} href The URL to navigate to.
 * @param {Object} state Optional state object to merge with current state. If not provided, preserves current state.
 */
export function navigate(href, state = null) {
  // Use SPA-friendly path resolution for relative paths
  const resolvedPath = resolveRelativePath(href);
  const newUrl = new URL(resolvedPath, window.location.origin);
  
  // Preserve current state or merge with provided state
  const currentState = history.state || {};
  const newState = state !== null ? { ...currentState, ...state } : currentState;
  
  history.pushState(newState, '', newUrl);
  handleNavigation(newUrl);
}

/**
 * Programmatically redirects to a new URL, replacing the current entry in browser history.
 * @param {string} href The URL to redirect to.
 */
export function redirect(href) {
  // Use SPA-friendly path resolution for relative paths
  const resolvedPath = resolveRelativePath(href);
  const newUrl = new URL(resolvedPath, window.location.origin);
  
  // Preserve existing history state so route-level UI state (filters, selectedRoleId, etc.)
  // is not wiped when a child route redirects to its default tab URL.
  const currentState = history.state || {};
  history.replaceState(currentState, '', newUrl);
  handleNavigation(newUrl);
}

/**
 * Updates the history state and triggers a re-render of the current route.
 * Useful for updating application state (like filters) without changing the URL.
 * @param {Object} state The state object to merge with existing history state.
 */
export function updateState(state) {
  const currentState = history.state || {};
  const newState = { ...currentState, ...state };
  history.replaceState(newState, '', window.location.href);
  handleNavigation(currentUrl);
}

// Expose internals for testing
export const __routerInternals = {
  renderRouteTree,
};
